<template>
    <app-layout>
        <base-header :back-url="route('stores.budgets.index', [store.id])" :shadow="false" border
                     border-bg-from="from-yellow-500"
                     border-bg-to="to-orange-500"
        >
            <div class="flex items-center justify-between w-full">
                <div v-if="budget">
                    <h4 class="text-xl font-medium flex space-x-2 items-center">
                        {{ $t('general.budget') }} {{ budget.year }}
                        <span v-if="budget && budget.name">
                            &nbsp;- {{ budget.name }}
                        </span>

                        <span class="h-full flex items-center">
                            &nbsp;<status-badge :budget="budget"/>
                        </span>
                    </h4>
                    <div class="flex flex-col">
                        <span v-if="!auth.user.isAdmin || auth.user.stores.length === 1">
                            {{ store.name }}
                        </span>

                        <store-navigation
                            v-else
                            :store="store"
                            @navigate="$inertia.visit(route('stores.budget-projections.index', {store: $event.id, year: budget.year}))"
                        />
                    </div>
                </div>
                <div v-else class="flex flex-col space-y-1">
                    <element-placeholder height="h-6" width="w-80"/>
                    <element-placeholder width="w-96"/>
                    <element-placeholder width="w-64"/>
                </div>

                <action-bar v-if="budget && !budget.is_locked">
                    <default-button
                        :disabled="budget.is_in_review"
                        :label="budget.is_locked ? $t('review.already_in_review') : null"
                        :loading="isSendingToReview"
                        icon="mail"
                        @click.native="sendToReview"
                    >
                        {{ $t('review.send_to_review') }}
                    </default-button>
                </action-bar>
            </div>
        </base-header>

        <data-table :actions="actions"
                    :auth="auth"
                    :collection="collection"
                    :delete-modal-title="$t('budget.delete_confirm')"
                    :additional-columns="additionalColumns"
                    :fixed-additional-columns-count="additionalColumns ? additionalColumns.columns.length : 0"
                    :is-loading="isLoadingBudget"
                    has-comparison-columns
        >
            <template #title-actions>
                <default-button
                    :label="showProjectionColumns ? $t('storemanager.hide-projection-columns') : $t('storemanager.show-projection-columns')"
                    icon="folder"
                    size="xxs"
                    :color="showProjectionColumns ? 'green' : 'orange'"
                    @click.native="toggleShowProjectionColumns"
                ></default-button>
            </template>

            <template v-slot:column-header="slot">
                <template v-if="slot.column.header">
                    <div v-html="slot.column.header"></div>
                </template>

                <template v-else>
                    {{ $moment(slot.column.entity.date).format('MMMM') }}
                </template>
            </template>
            <template v-slot:column-actions="slot">
                <copy-values :column="slot.column" :collection="collection" is-budget-page/>
            </template>
        </data-table>

        <div v-if="!isLoadingBudget && budget && budget.reviews && budget.reviews.length > 0"
             class="w-full border-t border-gray-300 dark:border-gray-800"
        >
            <base-card :is-default-open="false" flush is-closeable no-padding>
                <template #header>
                    <span class="px-4 py-2">
                        {{ $t('general.reviews') }}
                    </span>
                </template>
                <template #body>
                    <table-base class="text-sm">
                        <template #head>
                            <table-head>{{ $t('general.status') }}</table-head>
                            <table-head>{{ $t('general.reviewer') }}</table-head>
                            <table-head>{{ $t('review.reviewed_at') }}</table-head>
                            <table-head>{{ $t('general.comment') }}</table-head>
                        </template>
                        <template #body>
                            <table-row v-for="review in budget.reviews" :key="review.id">
                                <table-cell>
                                    <status-badge-review :review="review"/>
                                </table-cell>
                                <table-cell>
                                    {{ review.reviewer ? review.reviewer.name : null }}
                                </table-cell>
                                <table-cell>
                                    <template v-if="review.reviewed_at">
                                        {{ $moment.utc(review.reviewed_at).local().format('D. MMMM Y H:mm:ss') }}
                                    </template>
                                </table-cell>
                                <table-cell>
                                    {{ review.comment }}
                                </table-cell>
                            </table-row>
                        </template>
                    </table-base>
                </template>
            </base-card>
        </div>
    </app-layout>
</template>

<script>
import BudgetEntry from '@/Models/BudgetEntry';
import Budget from '@/Models/Budget';
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import DataColumnActions from '@/Components/Storemanager/Entities/DataColumnActions';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import Store from '@/Models/Store';
import StoreNavigation from '@/Pages/Stores/Components/StoreNavigation';
import StatusBadge from '@/Pages/Stores/Budgets/Components/StatusBadge';
import StatusBadgeReview from '@/Pages/Reviews/Components/StatusBadge';
import BudgetProjection from '@/Models/BudgetProjection';
import Actual from '@/Models/Actual';
import moment from 'moment';
import FieldsModel from '@/Models/Contracts/FieldsModel';
import CopyValues from '@/Pages/Stores/ProfitAndLoss/Components/CopyValues';

export default {
    components: {CopyValues, StatusBadge, StatusBadgeReview, StoreNavigation, DataTable},

    props: {
        store: Store,
        budget_id: String,
    },

    metaInfo() {
        return {
            title: this.$t('general.budget') +
                (!!this.budget ? (' - ' + this.budget.name + ' ' + this.budget.year) : ''),
        };
    },

    data: function() {
        return {
            isDeleting: [],
            collection: new DataColumnCollection(),
            actions: null,
            budget: null,
            budgetProjection: null,
            actuals: [],
            isLoadingBudget: true,
            isSendingToReview: false,
            showProjectionColumns: true,
        };
    },

    created() {
        Budget.with(['entries', 'reviews']).$find(this.budget_id).then(async budget => {
            this.budget = budget;
            let budgetPreviousYear = budget.year - 1;
            let allMonths = _.range(0, 12)
                .map(num => moment.utc().year(budgetPreviousYear).month(num).startOf('month').format('YYYY-MM-DD'));

            let budgetProjectionRequest = BudgetProjection.with(['entries'])
                .where('store_id', this.store.id).where('year', budgetPreviousYear).$first();

            let actualsRequest = Actual.where('store_id', this.store.id).whereIn('date', allMonths).$get();

            Promise.all([budgetProjectionRequest, actualsRequest]).then(responses => {
                this.budgetProjection = responses[0];
                this.actuals = responses[1];

                this.isLoadingBudget = false;
                this.createCollection();
            });
        });

        this.createActions();
    },

    computed: {
        additionalColumns() {
            if (!this.accumulatedColumn && !this.yearToDateColumn) {
                return null;
            }

            let columns = [];

            if (this.yearToDateColumn) {
                columns.push(this.yearToDateColumn);
            }

            if (this.accumulatedColumn) {
                columns.push(this.accumulatedColumn);
            }

            return new DataColumnCollection(columns, true);
        },

        accumulatedColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(
                this.collection.columns
                    .filter(column => column.entity instanceof BudgetEntry)
                    .map(column => column.entity),
                false,
            );

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.overall').toString();

            return column;
        },

        yearToDateColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(
                this.collection.columns
                    .filter(column => !(column.entity instanceof BudgetEntry))
                    .map(column => column.entity),
                false,
            );

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.ytd').toString() + ' ' + (this.budget.year - 1);

            return column;
        },
    },

    methods: {
        toggleShowProjectionColumns() {
            this.showProjectionColumns = !this.showProjectionColumns;
            this.createCollection();
        },

        sendToReview() {
            this.isSendingToReview = true;

            axios.post(route('api.budgets.review', [this.budget_id])).then(response => {
                response.data.budget.entries.map(entry => new BudgetEntry(entry));
                this.isSendingToReview = false;
                this.isLoadingBudget = true;

                Budget.with(['entries', 'reviews']).$find(this.budget_id).then(budget => {
                    this.budget = budget;
                    this.isLoadingBudget = false;
                    this.createCollection();
                });
            });
        },

        createCollection() {
            let collection = [];
            let monthRange = _.range(1, 13);

            for (const monthIndex of monthRange) {
                let column = new DataColumn();

                if (this.budget.entries && this.budget.entries.length > 0) {
                    column.entity = this.budget.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budget.year, 'M YYYY'), 'month'),
                    );
                }

                if (!column.entity) {
                    column.entity = new BudgetEntry({
                        store: this.store,
                        date: moment.utc(monthIndex + '-' + this.budget.year, 'M-Y')
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                    }).for(this.budget);
                }

                collection.push(column);

                // Add projection column from previous year if available
                if (!this.showProjectionColumns) {
                    continue;
                }

                let projectionColumn = new DataColumn();

                if (this.actuals && Array.isArray(this.actuals) && this.actuals.length > 0) {
                    projectionColumn.entity = this.actuals.find(entry => moment.utc(entry.date)
                        .isSame(moment.utc(monthIndex + ' ' + (this.budget.year - 1), 'M YYYY'), 'month'));
                }

                if (
                    !projectionColumn.entity
                    && this.budgetProjection
                    && this.budgetProjection.entries
                    && this.budgetProjection.entries.length > 0
                ) {
                    projectionColumn.entity = this.budgetProjection.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + (this.budget.year - 1), 'M YYYY'), 'month'),
                    );
                }

                if (projectionColumn.entity) {
                    projectionColumn.isLocked = true;
                    projectionColumn.header = projectionColumn.entity.header;
                    collection.push(projectionColumn);
                }
            }

            this.collection = new DataColumnCollection(collection,
                (this.budget?.is_locked ? this.budget?.is_locked : this.budget?.is_in_review) ?? false);
        },

        createActions() {
            this.actions = new DataColumnActions();

            this.actions.resetCallback = async (column) => {
                if (column.entity && column.entity.id) {
                    column.isLoading = true;
                    column.entity = await BudgetEntry.$find(column.entity.id);
                    column.isLoading = false;
                } else {
                    column.entity = new BudgetEntry({
                        store: this.store,
                        date: moment.utc(column.entity.date).startOf('month').format('YYYY-MM-DD'),
                    }).for(this.budget);
                }

                column.isEditing = false;
            };

            this.actions.deleteCallback = async (column) => {
                column.isLoading = true;
                await column.entity.delete();

                column.entity = new BudgetEntry({
                    store: this.store,
                    date: moment.utc(column.entity.date).startOf('month').format('YYYY-MM-DD'),
                }).for(this.budget);

                column.isLoading = false;
            };
        },
    },
};
</script>
